import { isAxiosError } from "axios";

type ApiErrorDetail = {
  message?: unknown;
};

type ApiErrorPayload = {
  message?: unknown;
  errors?: ApiErrorDetail[];
};

const APPLICATION_SERIAL_NUMBER_PATTERN = /^\s*APP-[A-Z0-9][A-Z0-9-]*\s*$/i;
const BRACKET_CONTENT_PATTERN = /\[([^\]]+)\]/g;

function findApplicationSerialNumber(value: unknown): string | undefined {
  if (typeof value === "string") {
    for (const [, content] of value.matchAll(BRACKET_CONTENT_PATTERN)) {
      if (APPLICATION_SERIAL_NUMBER_PATTERN.test(content)) {
        return content.trim();
      }
    }

    return undefined;
  }

  if (!value || typeof value !== "object") {
    return undefined;
  }

  if (Array.isArray(value)) {
    for (const item of value) {
      const serialNumber = findApplicationSerialNumber(item);
      if (serialNumber) {
        return serialNumber;
      }
    }
    return undefined;
  }

  for (const item of Object.values(value)) {
    const serialNumber = findApplicationSerialNumber(item);
    if (serialNumber) {
      return serialNumber;
    }
  }

  return undefined;
}

export function getApiErrorMessage(
  error: unknown,
  fallback = "An unexpected error occurred.",
): string {
  if (isAxiosError<ApiErrorPayload>(error)) {
    const payload = error.response?.data;
    const detailMessages = payload?.errors
      ?.map((detail) =>
        typeof detail.message === "string" ? detail.message : undefined,
      )
      .filter((message): message is string => Boolean(message));

    if (detailMessages?.length) {
      return detailMessages.join("\n");
    }

    if (typeof payload?.message === "string") {
      return payload.message;
    }
  }

  if (error instanceof Error && error.message) {
    return error.message;
  }

  return fallback;
}

export function getApiErrorApplicationSerialNumber(
  error: unknown,
): string | undefined {
  if (isAxiosError<ApiErrorPayload>(error)) {
    const serialNumber = findApplicationSerialNumber(error.response?.data);
    if (serialNumber) {
      return serialNumber;
    }
  }

  if (error instanceof Error) {
    return findApplicationSerialNumber(error.message);
  }

  return undefined;
}
